import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Avatar,
  Chip,
  Alert,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import { buildTriviaLeaderboardRows } from "../../utils/triviaLeaderboardRows";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  formatGridStatValue,
  getResultsGridSx,
  sortedClassFor,
} from "../../utils/resultsGrid";

const TRIVIA_TABLE_HEIGHT = 560;

const UsernameCell = ({ username }) => (
  <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
    <Avatar
      sx={{
        width: 32,
        height: 32,
        mr: 1,
        bgcolor: "primary.main",
        fontSize: "0.875rem",
      }}
    >
      {username?.charAt(0)?.toUpperCase() || "?"}
    </Avatar>
    <Typography variant="body2" fontWeight={600} noWrap title={username}>
      {username}
    </Typography>
  </Box>
);

const LeaderboardTable = ({ data, type, loading }) => {
  const [sortModel, setSortModel] = useState([
    {
      field:
        type === "streak"
          ? "bestStreak"
          : type === "speed"
            ? "averageTimePerQuestion"
            : "totalScore",
      sort: "desc",
    },
  ]);
  const sortedField = sortModel[0]?.field;
  const rows = useMemo(
    () => buildTriviaLeaderboardRows(data?.rankings?.slice(0, 10) || [], type),
    [data, type]
  );

  const columns = useMemo(() => {
    const base = [
      {
        field: "rank",
        headerName: "#",
        width: 56,
        sortable: false,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "username",
        headerName: "Player",
        flex: 1.2,
        minWidth: 160,
        headerClassName: sortedClassFor("username", sortedField),
        cellClassName: sortedClassFor("username", sortedField),
        sortingOrder: NAME_SORT_ORDER,
        renderCell: (params) => <UsernameCell username={params.value} />,
      },
      {
        field: "level",
        headerName: "Level",
        width: 110,
        headerClassName: sortedClassFor("level", sortedField),
        cellClassName: sortedClassFor("level", sortedField),
        sortingOrder: STAT_SORT_ORDER,
        renderCell: (params) => (
          <Chip
            label={`Level ${params.value || 1}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        ),
      },
    ];

    if (type === "streak") {
      return [
        ...base,
        {
          field: "bestStreak",
          headerName: "Best Streak",
          width: 130,
          type: "number",
          align: "right",
          headerAlign: "right",
          headerClassName: sortedClassFor("bestStreak", sortedField),
          cellClassName: sortedClassFor("bestStreak", sortedField),
          sortingOrder: STAT_SORT_ORDER,
        },
        {
          field: "currentStreak",
          headerName: "Current",
          width: 110,
          type: "number",
          align: "right",
          headerAlign: "right",
          headerClassName: sortedClassFor("currentStreak", sortedField),
          cellClassName: sortedClassFor("currentStreak", sortedField),
          sortingOrder: STAT_SORT_ORDER,
        },
        {
          field: "totalGamesPlayed",
          headerName: "Games",
          width: 110,
          type: "number",
          align: "right",
          headerAlign: "right",
          headerClassName: sortedClassFor("totalGamesPlayed", sortedField),
          cellClassName: sortedClassFor("totalGamesPlayed", sortedField),
          sortingOrder: STAT_SORT_ORDER,
        },
      ];
    }

    if (type === "speed") {
      return [
        ...base,
        {
          field: "averageTimePerQuestion",
          headerName: "Avg Time",
          width: 120,
          type: "number",
          align: "right",
          headerAlign: "right",
          headerClassName: sortedClassFor("averageTimePerQuestion", sortedField),
          cellClassName: sortedClassFor("averageTimePerQuestion", sortedField),
          sortingOrder: STAT_SORT_ORDER,
          renderCell: (params) => formatGridStatValue(params.value, "seconds"),
        },
        {
          field: "totalGames",
          headerName: "Games",
          width: 110,
          type: "number",
          align: "right",
          headerAlign: "right",
          headerClassName: sortedClassFor("totalGames", sortedField),
          cellClassName: sortedClassFor("totalGames", sortedField),
          sortingOrder: STAT_SORT_ORDER,
        },
        {
          field: "fastestGame",
          headerName: "Fastest",
          width: 120,
          type: "number",
          align: "right",
          headerAlign: "right",
          headerClassName: sortedClassFor("fastestGame", sortedField),
          cellClassName: sortedClassFor("fastestGame", sortedField),
          sortingOrder: STAT_SORT_ORDER,
          renderCell: (params) => formatGridStatValue(params.value, "seconds"),
        },
      ];
    }

    return [
      ...base,
      {
        field: "totalScore",
        headerName: "Score",
        width: 120,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor("totalScore", sortedField),
        cellClassName: sortedClassFor("totalScore", sortedField),
        sortingOrder: STAT_SORT_ORDER,
        renderCell: (params) => formatGridStatValue(params.value, "count"),
      },
      {
        field: "gamesPlayed",
        headerName: "Games",
        width: 110,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor("gamesPlayed", sortedField),
        cellClassName: sortedClassFor("gamesPlayed", sortedField),
        sortingOrder: STAT_SORT_ORDER,
      },
      {
        field: "accuracy",
        headerName: "Accuracy",
        width: 120,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor("accuracy", sortedField),
        cellClassName: sortedClassFor("accuracy", sortedField),
        sortingOrder: STAT_SORT_ORDER,
        renderCell: (params) => formatGridStatValue(params.value, "percent"),
      },
    ];
  }, [sortedField, type]);

  if (!loading && (!data?.rankings || data.rankings.length === 0)) {
    return (
      <Alert severity="info">No data available for this leaderboard yet.</Alert>
    );
  }

  return (
    <Paper sx={{ overflow: "hidden" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        sortingMode="client"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        rowHeight={52}
        sx={getResultsGridSx(TRIVIA_TABLE_HEIGHT)}
      />
    </Paper>
  );
};

const TriviaLeaderboard = ({
  globalLeaderboard,
  streakLeaderboard,
  speedLeaderboard,
  isLoading,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Leaderboards
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant="fullWidth"
        sx={{ mb: 3 }}
      >
        <Tab icon={<TrophyIcon />} label="Global" iconPosition="start" />
        <Tab icon={<TimelineIcon />} label="Streaks" iconPosition="start" />
        <Tab icon={<SpeedIcon />} label="Speed" iconPosition="start" />
      </Tabs>

      {activeTab === 0 && (
        <LeaderboardTable
          data={globalLeaderboard}
          type="global"
          loading={isLoading}
        />
      )}
      {activeTab === 1 && (
        <LeaderboardTable
          data={streakLeaderboard}
          type="streak"
          loading={isLoading}
        />
      )}
      {activeTab === 2 && (
        <LeaderboardTable
          data={speedLeaderboard}
          type="speed"
          loading={isLoading}
        />
      )}
    </Box>
  );
};

export default TriviaLeaderboard;
